import { useEffect, useState } from 'react'
import { useAccountContext } from '../contexts'
import { api } from '../services'
import { roundToDecimal } from '../common/utils'
import { SensorPort, UnitCategoryId } from '../common/enums'
import { convertBaseToDisplay } from '../common/conversions'
import { Spinner } from './Spinner'

interface LastReadingData {
  productName: string
  readings: Array<string>
}

let infoWindow: google.maps.InfoWindow

export function Marker(options: any) {
  const selectDevice: (sigfoxId: string) => void = options.selectDevice
  const selected = options.selected

  const { userUnitSettings, unitConversions, selectedDevice, devices } = useAccountContext()

  // const marker = new google.maps.marker.AdvancedMarkerElement()
  const marker = new google.maps.Marker()
  infoWindow = infoWindow ?? new google.maps.InfoWindow()

  // const [device, setDevice] = useState<DeviceData>(options.device)
  const [lastReadings, setLastReadings] = useState<Record<string, LastReadingData>>({})
  const [lastReadingTimestamp, setLastReadingTimestamp] = useState<Date>(new Date())
  const [isAquaTrollSensor, setIsAquaTrollSensor] = useState(false)
  const [isWeatherStation, setIsWeatherStation] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const device = devices[options.sigfoxId]

  useEffect(() => {
    if (
      selectedDevice === device.sigfoxId &&
      selected &&
      userUnitSettings !== undefined &&
      unitConversions !== undefined
    ) {
      setIsLoading(true)
      api
        .getLastReadings(device.sigfoxId)
        .then((result: any) => {
          const lastReadings = formatLastReadings(result.data)
          setIsAquaTrollSensor(checkForAquaTroll(lastReadings))
          setIsWeatherStation(checkForWeatherStation(lastReadings))

          setLastReadings(lastReadings)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }

    return () => {
      setIsLoading(false)
    }
  }, [selectedDevice, userUnitSettings, unitConversions]) // eslint-disable-line react-hooks/exhaustive-deps

  if (marker) {
    marker.setOptions(options)

    marker.addListener('click', () => {
      selectDevice(device.sigfoxId)
    })
  }

  useEffect(() => {
    let htmlContent = ''

    if (isLoading) {
      htmlContent += `
      <div style="width: 210px">
        <b>${device.deviceName}</b>
        <br>Site: ${device.siteName}
        <br>Last Reading: ...
        <br>A1: ...
        <br>A2: ...
        <br>B1: ...
        <br>B2: ...
        <br>C1: ...
        <br>C2: ...
    `
      infoWindow.setContent(htmlContent)
      infoWindow.open({
        anchor: marker,
        shouldFocus: true,
      })
      return
    }

    htmlContent += `
      <div>
        <b>${device.deviceName}</b>
        <br>Site: ${device.siteName}
        <br>Last Reading: ${lastReadingTimestamp.toLocaleString()}
        <br>${getSensorData(SensorPort.A1)}
        <br>${getSensorData(SensorPort.A2)}
        <br>${getSensorData(SensorPort.B1)}
        <br>${getSensorData(SensorPort.B2)}
        <br>${getSensorData(SensorPort.C1)}
        <br>${getSensorData(SensorPort.C2)}
    `

    if (isAquaTrollSensor) {
      htmlContent += `<br>${getSensorData(SensorPort.C3)}`
    }

    if (isWeatherStation) {
      htmlContent += `<br>${getSensorData(SensorPort.T0)}`
    }

    htmlContent += '</div>'

    // setContent(htmlContent)
    infoWindow.setContent(htmlContent)
    infoWindow.close()

    device.sigfoxId === selectedDevice
      ? infoWindow.open({
          anchor: marker,
          shouldFocus: true,
        })
      : infoWindow.close()

    function getSensorData(sensorPort: SensorPort) {
      const port = sensorPort === SensorPort.C3 ? 'T0' : sensorPort

      if (lastReadings[port] && lastReadings[port].readings.length > 0) {
        let productName = lastReadings[port].productName
        if (isAquaTrollSensor && sensorPort === SensorPort.C3) {
          productName = 'AquaTROLL Water Temperature'
        }
        return `${sensorPort === 'T0' ? 'C3' : sensorPort}: ${productName} -- ${lastReadings[port].readings.join(', ')}`
      }
      return `${sensorPort === 'T0' ? 'C3' : sensorPort}: Not Connected`
    }
  }, [isLoading, device, selectedDevice, lastReadings, lastReadingTimestamp, isAquaTrollSensor, isWeatherStation]) // eslint-disable-line react-hooks/exhaustive-deps

  function formatLastReadings(response: any) {
    const result: Record<string, LastReadingData> = {}

    const isAquaTrollSensor = response['A1-21'] !== undefined

    Object.keys(response).forEach((sensorPortAndUnitCategoryId: string) => {
      const [sensorPort] = sensorPortAndUnitCategoryId.split('-')
      const { readings, metadata } = response[sensorPortAndUnitCategoryId]

      if (result[sensorPort] === undefined) {
        result[sensorPort] = {
          productName: metadata.productName,
          readings: [],
        }
      }

      if (Object.keys(readings).length > 0) {
        const timestamp = Number(Object.keys(readings).reverse()[0])

        setLastReadingTimestamp(new Date(timestamp * 1000))

        let unitCategoryId = metadata.units.category.id

        if (isAquaTrollSensor && sensorPort === 'T0') {
          unitCategoryId = UnitCategoryId.WaterTemperature
        }

        const { unitId, precision } = userUnitSettings[unitCategoryId]
        const unit = unitConversions[unitCategoryId as UnitCategoryId].abbreviations[unitId]
        const formula = unitConversions[unitCategoryId as UnitCategoryId].formulas[unitId]
        let value = 0

        if (isAquaTrollSensor && sensorPort === 'T0') {
          value = Number(Object.values(readings).reverse()[0])
          value = (value + 40) * 32 // reverse old temperature conversion
          value = value / 50 - 5 // use AquaTroll500TemperatureSensor as per specification (even though the C3 scaling parameters are not sent from the firmware)
          value = roundToDecimal(value, precision)
        } else {
          value = Number(Object.values(readings).reverse()[0])
        }

        const lastReading = roundToDecimal(convertBaseToDisplay(value, formula), precision)

        result[sensorPort].readings.push(`${lastReading} ${unit}`)
      }
    })

    return result
  }

  function checkForAquaTroll(lastReadings: any) {
    const checks: Record<string, boolean> = {
      'aquatroll dissolved oxygen': false,
      'aquatroll turbidity': false,
      'aquatroll nitrate': false,
      'aquatroll salinity': false,
      'aquatroll total dissolved solids': false,
      'aquatroll specific conductivity': false,
    }

    Object.values(lastReadings)
      .map((item: any) => item.productName.toLowerCase())
      .forEach((productName: string) => {
        if (checks[productName] !== undefined) {
          checks[productName] = true
        }
      })

    return Object.values(checks).every((value) => value === true)
  }

  function checkForWeatherStation(lastReadings: any) {
    const checks: Record<string, boolean> = {
      'wind speed': false,
      'wind direction': false,
      'solar radiation': false,
      rainfall: false,
      pressure: false,
      humidity: false,
    }

    Object.values(lastReadings)
      .map((item: any) => item.productName.toLowerCase())
      .forEach((productName: string) => {
        if (checks[productName] !== undefined) {
          checks[productName] = true
        }
      })

    return Object.values(checks).every((value) => value === true)
  }

  // right align
  return <div className="flex flex-row-reverse">{isLoading ? <Spinner size={25} /> : null}</div>
}
